import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Popover,
  Button,
  Switch,
  Stack,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import GridViewIcon from '@mui/icons-material/GridView';

const Header = ({
  Icon,
  title,
  subtitle,
  showAddButton,
  addButtonText,
  onAddClick,
  showActions,
  actionOptions,
  selectedAction,
  onActionChange,
  onDeleteClick,
  onRefresh,
  openFilter,
  handleFilterClick,
  handleFilterClose,
  anchorElFilter,
  handleSortClick,
  handleSort,
  openSort,
  anchorElSort,
  handleSortClose,
  handleOptionsClick,
  columns,
  openOptions,
  anchorElOptions,
  handleOptionsClose,
  handleAttributesClick,
  handleSettingsClick,
  openAttributes,
  anchorElAttributes,
  handleAttributesClose,
  visibilityStatus,
  handleToggle,
  selectedRows,
  onModifyClick,
  object,
  layoutView,
  onLayoutChange,
  openLayout,
  anchorElLayout,
  handleLayoutClick,
  handleLayoutClose,
}) => {
  const theme = useTheme();

  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 28,
    height: 16,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": { width: 15 },
      "& .MuiSwitch-switchBase.Mui-checked": { transform: "translateX(9px)" },
    },
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(12px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor: "#1890ff",
          ...theme.applyStyles("dark", { backgroundColor: "#177ddc" }),
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: theme.transitions.create(["width"], { duration: 200 }),
    },
    "& .MuiSwitch-track": {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: "rgba(0,0,0,.25)",
      boxSizing: "border-box",
      ...theme.applyStyles("dark", { backgroundColor: "rgba(255,255,255,.35)" }),
    },
  }));

  return (
    // ── Gradient wrapper — gives every page that uses <Header> the blue glow ──
    <Box
      sx={{
        background:
          "radial-gradient(circle at center, rgba(0,40,255,0.25) 0%, rgba(0,0,0,0) 70%)",
        width: "100%",
        minHeight:220
      }}
    >
      {/* ── Inner row: title left, actions right ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: "32px",
          pt: 4,
          pb: 4,
        }}
      >
        {/* Left — icon + title + subtitle */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            {Icon && (
              <Icon
                style={{
                  height: "32px",
                  width: "32px",
                  color: theme.palette.text.color,
                }}
              />
            )}
            <Typography
              sx={{
                mt:1,
                fontSize: "30px",
                fontWeight: "normal",
                color: theme.palette.text.color,
              }}
            >
              {title}
            </Typography>
          </Box>
          {subtitle && (
            <Typography sx={{ mt: 1, fontSize: "14px", color: "#8A8A8A" }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Right — buttons & icon actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {showAddButton && (
            <Button
              sx={{
                fontSize: 14,
                color: "#fff",
                height: "36px",
                fontWeight: 400,
                padding: "0px 16px",
                whiteSpace: "nowrap",
                minWidth: "auto",
                textTransform: "none",
                backgroundColor:
                  selectedRows?.length > 0
                    ? theme.palette.tertiary.main
                    : "#006BCE",
                "&:hover": { backgroundColor: "#0058A5" },
                "& .MuiButton-startIcon": { marginLeft: 0, marginRight: "4px" },
              }}
              disabled={selectedRows?.length > 0}
              startIcon={<AddIcon />}
              onClick={onAddClick}
            >
              {addButtonText}
            </Button>
          )}

          {showActions && (
            <FormControl sx={{ width: 150, margin: "0 10px" }}>
              <Select
                value=""
                displayEmpty
                renderValue={() => (
                  <span style={{ color: "#006BCE", fontSize: "14px" }}>
                    Actions
                  </span>
                )}
                sx={{
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#006BCE",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#006BCE" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#006BCE" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#006BCE" },
                  "& .MuiSvgIcon-root": { color: "#006BCE" },
                  "& .MuiSelect-select": { padding: "8px 12px" },
                }}
              >
                {actionOptions?.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{ fontSize: "14px" }}
                    onClick={() => {
                      if (option.onClick) option.onClick();
                      if (option.label === "Delete") onDeleteClick();
                      if (option.label === "Modify") onModifyClick();
                    }}
                    disabled={
                      option.disabled !== undefined
                        ? option.disabled
                        : (option.label === "Modify" &&
                            (selectedRows?.length === 0 || selectedRows?.length > 1)) ||
                          (option.label === "Delete" && selectedRows?.length === 0)
                    }
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {onRefresh &&
          <IconButton onClick={() => onRefresh({ refresh: true })}>
            <RefreshIcon />
          </IconButton>
          }
          {handleFilterClick&&
          <IconButton onClick={handleFilterClick}>
            <FilterListIcon />
          </IconButton>
          }
          {handleOptionsClick&&
          <IconButton onClick={handleOptionsClick}>
            <MoreHorizIcon />
          </IconButton>}
        </Box>

        {/* ── Popovers (unchanged) ── */}

        {/* Filter */}
        <Popover
          open={openFilter}
          anchorEl={anchorElFilter}
          onClose={handleFilterClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "12px 14px", bgcolor: theme.palette.secondary.main, color: "#fff", width: "200px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Typography sx={{ color: theme.palette.text.color }}>Filter</Typography>
              <CloseIcon sx={{ color: theme.palette.text.color, fontSize: "14px", cursor: "pointer" }} onClick={handleFilterClose} />
            </Box>
            <Divider sx={{ backgroundColor: theme.palette.border.color, boxSizing: "border-box", padding: "0px 14px" }} />
            {["Last Scanned", "Status", "Type"].map((label) => (
              <Button key={label} sx={{ textTransform: "none", color: theme.palette.text.color, justifyContent: "flex-start", textAlign: "left", padding: "6px 14px", width: "100%", "&:hover": { bgcolor: theme.palette.primary.main } }}>
                {label}
              </Button>
            ))}
          </Box>
        </Popover>

        {/* Sort */}
        <Popover
          open={openSort}
          anchorEl={anchorElSort}
          onClose={handleSortClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "12px 14px", bgcolor: theme.palette.secondary.main, color: "#fff", width: "200px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Typography sx={{ color: theme.palette.text.color }}>Sort</Typography>
              <CloseIcon sx={{ color: theme.palette.text.color, fontSize: "14px", cursor: "pointer" }} onClick={handleSortClose} />
            </Box>
            <Divider sx={{ backgroundColor: theme.palette.border.color, boxSizing: "border-box", padding: "0px 14px" }} />
            <Button sx={{ textTransform: "none", color: theme.palette.text.color, justifyContent: "flex-start", textAlign: "left", padding: "6px 14px", width: "100%", "&:hover": { bgcolor: theme.palette.primary.main } }} onClick={() => handleSort("asc")}>By Ascending</Button>
            <Button sx={{ textTransform: "none", color: theme.palette.text.color, justifyContent: "flex-start", textAlign: "left", padding: "6px 14px", width: "100%", "&:hover": { bgcolor: theme.palette.primary.main } }} onClick={() => handleSort("dsc")}>By Descending</Button>
          </Box>
        </Popover>

        {/* More Options */}
        <Popover
          open={openOptions}
          anchorEl={anchorElOptions}
          onClose={handleOptionsClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", padding: "12px 14px", gap: 1, bgcolor: theme.palette.secondary.main, color: "#fff", width: "200px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Typography sx={{ color: theme.palette.text.color }}>Options</Typography>
              <CloseIcon sx={{ color: theme.palette.text.color, fontSize: "14px", cursor: "pointer" }} onClick={handleOptionsClose} />
            </Box>
            <Divider sx={{ backgroundColor: theme.palette.border.color, boxSizing: "border-box", padding: "0px 14px" }} />
            <Button sx={{ textTransform: "none", color: theme.palette.text.color, justifyContent: "flex-start", textAlign: "left", padding: "6px 14px", width: "100%", "&:hover": { bgcolor: theme.palette.primary.main } }}>Export</Button>
            <Button sx={{ textTransform: "none", color: theme.palette.text.color, justifyContent: "flex-start", textAlign: "left", padding: "6px 14px", width: "100%", "&:hover": { bgcolor: theme.palette.primary.main } }} onClick={handleAttributesClick}>Attributes</Button>
            <Button sx={{ textTransform: "none", color: theme.palette.text.color, justifyContent: "flex-start", textAlign: "left", padding: "6px 14px", width: "100%", "&:hover": { bgcolor: theme.palette.primary.main } }} onClick={handleSettingsClick}>Settings</Button>
            <Button
              sx={{ textTransform: "none", color: theme.palette.text.color, textAlign: "left", padding: "6px 14px", width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", "&:hover": { bgcolor: theme.palette.primary.main } }}
              onClick={handleLayoutClick}
            >
              <span>Layout</span>
              <Box sx={{ display: "flex" }}>
                <span style={{ fontSize: "12px", color: "#fff", opacity: 0.7 }}>{layoutView} </span>
                <ChevronRightIcon sx={{ fontSize: "20px", color: "#fff" }} />
              </Box>
            </Button>
          </Box>
        </Popover>

        {/* Layout */}
        <Popover
          open={openLayout}
          anchorEl={anchorElLayout}
          onClose={handleLayoutClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", padding: "12px 14px", gap: 1, bgcolor: theme.palette.background?.paper || "#fff", color: theme.palette.text.primary, width: "200px", border: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", cursor: "pointer" }} onClick={handleLayoutClose}>
                <NavigateBeforeIcon sx={{ color: theme.palette.text.primary, fontSize: "20px" }} />
                <Typography sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>Layout</Typography>
              </Box>
              <CloseIcon sx={{ color: theme.palette.text.primary, fontSize: "14px", cursor: "pointer" }} onClick={handleLayoutClose} />
            </Box>
            <Divider />
            <Box sx={{ display: "flex", gap: 1, paddingY: 1 }}>
              {[
                { label: "List View", icon: MenuIcon, value: "list" },
                { label: "Grid View", icon: GridViewIcon, value: "grid" },
              ].map(({ label, icon: Icon, value }) => (
                <Button
                  key={value}
                  sx={{ textTransform: "none", color: theme.palette.text.primary, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", border: `1px solid ${theme.palette.divider}`, "&:hover": { bgcolor: theme.palette.action?.hover || "#f5f5f5", borderColor: "blue", "& .MuiSvgIcon-root": { color: "blue" } } }}
                  onClick={() => { onLayoutChange(value); handleLayoutClose(); }}
                >
                  <Icon sx={{ fontSize: "20px" }} />
                  <Typography sx={{ fontSize: "6px" }}>{label}</Typography>
                </Button>
              ))}
            </Box>
          </Box>
        </Popover>

        {/* Attributes */}
        <Popover
          open={openAttributes}
          anchorEl={anchorElAttributes}
          onClose={handleAttributesClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", padding: "12px 14px", gap: 1, bgcolor: theme.palette.secondary.main, color: "#fff", width: "200px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", cursor: "pointer" }} onClick={handleAttributesClose}>
                <NavigateBeforeIcon sx={{ color: theme.palette.text.color, fontSize: "20px" }} />
                <Typography sx={{ color: theme.palette.text.color }}>Attributes</Typography>
              </Box>
              <CloseIcon sx={{ color: theme.palette.text.color, fontSize: "14px", cursor: "pointer" }} onClick={handleOptionsClose} />
            </Box>
            <Divider sx={{ backgroundColor: theme.palette.border.color, boxSizing: "border-box", padding: "0px 14px" }} />
            <Box>
              {columns?.map((column) =>
                column.display ? (
                  <Box key={column.id} sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
                    <Button sx={{ textTransform: "none", color: theme.palette.text.color, justifyContent: "flex-start", textAlign: "left", padding: "8px 0px", width: "100%" }}>
                      {column.label}
                    </Button>
                    <Stack direction="row" sx={{ alignItems: "center" }}>
                      <AntSwitch
                        checked={visibilityStatus[column.id]}
                        onChange={handleToggle(column.id)}
                        inputProps={{ "aria-label": `Toggle ${column.label}` }}
                      />
                    </Stack>
                  </Box>
                ) : null
              )}
            </Box>
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default Header;